import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileText, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg } from "./api";
import DetailSVG from "./DetailSVG";
import { InfoLine } from "./InfoLine";
import GlassPicker from "./GlassPicker";

const KEYS = [["typical_mullion", "Typical mullion"], ["typical_transom", "Typical transom"], ["stack_joint", "Stack joint"], ["anchor_bracket", "Anchor bracket"]];
const HIDE = ["glass_inner_y", "warning", "series", "variant"];
const VARIANT_OPTS = [["SA", "SA · 50 mm outer box (8012 + 3459)"], ["SB", "SB · 19 mm low cap"], ["SC", "SC · flush silicone"]];

const Pick = ({ label, value, onChange, options, testid }) => (
  <label className="space-y-1">
    <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 border-zinc-800 bg-zinc-900 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}</SelectContent>
    </Select>
  </label>
);

export const DetailCard = ({ d }) => (
  <div className="rounded-lg border border-zinc-800 bg-[#0c0c0f] p-3" data-testid={`bway-detail-${d.key}`}>
    <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-zinc-400">{d.title}</div>
    {d.info && <InfoLine info={d.info} hide={HIDE} testid={`bway-detail-info-${d.key}`} />}
    {d.info?.warning && <div className="mb-2 rounded border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-200">{d.info.warning}</div>}
    <DetailSVG detail={d} />
  </div>
);

export default function BwayAssembly({ designId, result, inputs }) {
  const tube = result?.members[0]?.lib_id || "bway_8011";
  const [opts, setOpts] = useState({ variant: inputs?.bway_variant || "SA", series: inputs?.bway_series || "25100S" });
  const [glass, setGlass] = useState(inputs?.glass_type || "D-6FT-12-6FT");
  const [keys, setKeys] = useState(KEYS.map(([k]) => k));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const body = { ...opts, keys, glass_id: glass, mullion_id: tube, transom_id: result?.members[1]?.lib_id || undefined, design_id: designId || undefined };

  useEffect(() => {
    if (!keys.length) return;
    const t = setTimeout(() => api.post("/bway/assembly", body).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e, "Could not build details"))), 300);
    return () => clearTimeout(t);
  }, [opts, keys, glass, tube, designId]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (kind) => {
    setBusy(kind);
    try {
      const res = await api.post(`/bway/assembly/${kind}`, body, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `bway_slimpro_details.${kind}`;
      a.click();
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="bway-assembly">
      <div className="grid gap-3 rounded-lg border border-zinc-800 bg-[#121215] p-4 md:grid-cols-3">
        <Pick label="Outer variant" testid="bway-asm-variant" value={opts.variant} onChange={(v) => setOpts((o) => ({ ...o, variant: v }))} options={VARIANT_OPTS} />
        <div className="space-y-1">
          <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Mullion tube</span>
          <div className="flex h-9 items-center rounded-md border border-zinc-800 bg-zinc-900 px-3 font-mono text-xs text-zinc-300" data-testid="bway-asm-tube">{result?.members[0]?.profile} · {opts.series}</div>
        </div>
        <div className="md:row-span-2"><span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Glass (18–32 mm)</span><GlassPicker value={glass} onChange={setGlass} testid="bway-asm-glass" range={[18, 32.5]} /></div>
        <div className="flex flex-wrap gap-x-4 gap-y-2 md:col-span-2">
          {KEYS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-xs text-zinc-300">
              <Checkbox data-testid={`bway-asm-key-${k}`} checked={keys.includes(k)} onCheckedChange={(c) => setKeys((ks) => (c ? [...ks, k] : ks.filter((x) => x !== k)))} />{l}
            </label>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {data?.parts && <span className="font-mono text-xs text-sky-300" data-testid="bway-asm-parts">BWAY parts: {Object.entries(data.parts).map(([k, v]) => `${k.replace("_", " ")} ${v}`).join(" · ")}</span>}
        <div className="ml-auto flex gap-2">
          <Button data-testid="bway-asm-download-dxf" size="sm" variant="outline" disabled={!!busy || !data} onClick={() => download("dxf")} className="border-zinc-700 hover:text-white">{busy === "dxf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}Details DXF</Button>
          <Button data-testid="bway-asm-download-pdf" size="sm" variant="outline" disabled={!!busy || !data} onClick={() => download("pdf")} className="border-zinc-700 hover:text-white">{busy === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}Details PDF</Button>
        </div>
      </div>
      <div className="grid gap-4 2xl:grid-cols-2">{data?.details.map((d) => <DetailCard key={d.key} d={d} />)}</div>
    </div>
  );
}
